import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";

type PublicConfig = {
  demoMode: boolean;
  realModeLaunchDate: string | null;
};

function parseLaunchDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const isoCandidate = /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? `${trimmed}T00:00:00` : trimmed;
  const d = new Date(isoCandidate);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function formatCountdown(launch: Date): { primary: string; secondary: string } {
  const now = new Date();
  const diffMs = launch.getTime() - now.getTime();
  if (diffMs <= 0) return { primary: "Launching today", secondary: "Demo mode ending soon" };

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);

  if (days >= 2) {
    const launchLabel = launch.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return { primary: `${days} days until launch`, secondary: `Real mode starts ${launchLabel} — orders here are test only` };
  }
  if (days === 1) {
    return { primary: `1 day ${hours} hr until launch`, secondary: "Real mode starts tomorrow — orders here are test only" };
  }
  if (hours >= 1) {
    return { primary: `${hours} hr until launch`, secondary: "Real mode starts today — orders here are test only" };
  }
  const mins = Math.max(1, Math.floor(diffMs / (1000 * 60)));
  return { primary: `${mins} min until launch`, secondary: "Real mode is about to start — orders here are test only" };
}

export function CountdownBanner() {
  const { data: config } = useQuery<PublicConfig>({
    queryKey: ["/api/public/config"],
    queryFn: async () => {
      const res = await fetch("/api/public/config", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  if (!config?.demoMode) return null;
  const launch = parseLaunchDate(config.realModeLaunchDate);
  if (!launch) return null;
  if (launch.getTime() - Date.now() < -1000 * 60 * 60 * 24) return null;

  const { primary, secondary } = formatCountdown(launch);

  return (
    <div className="border-b border-primary/20 bg-primary/8 dark:bg-primary/15">
      <div className="container mx-auto flex items-center gap-2 px-4 py-2">
        <Sparkles className="h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold text-primary">{primary}</div>
          <div className="truncate text-[11px] text-muted-foreground">{secondary}</div>
        </div>
      </div>
    </div>
  );
}
